<template>
  <content-viewer :title="$t('all_order_detail')" modal-class="w-110">
    <template #trigger="{ open }">
      <nd-button type="text" class="active" size="small" icon="arrow-down" icon-position="right" :disabled="isSoldOut" @click="open">
        {{ $t('all_show_detail') }}
      </nd-button>
    </template>
    <template #default>
      <div class="scrollbar max-h-[calc(min(800px,50dvh)-128px)] -mx-5 px-5">
        <div class="product-name text-base">{{ props.product.product_name }}</div>
        <div class="duration text-sm mt-2 pb-4">
          <p v-if="startDateTimeString" class="start-time flex gap-2">
            <span>{{ $t('booking_time_begin') }}</span>
            <span>{{ startDateTimeString }}</span>
          </p>
          <p v-if="endDateTimeString" class="end-time flex gap-2">
            <span>{{ $t('booking_time_end') }}</span>
            <span>{{ endDateTimeString }}</span>
          </p>
        </div>
        <hr />
        <div class="total-price flex gap-3 justify-between py-4">
          <span>{{ totalPriceLabel }}</span>
          <span class="text-yellow-50 min-w-fit">{{ props.product?.currency_code }} {{ priceFormat(totalPrice) }}</span>
        </div>
        <hr />
        <!-- <div v-if="dayCount > 1"></div> -->
        <div v-if="props.product.is_crossday" class="booking-item-list">
          <template v-for="item in uiBookingItem" :key="item.dateTime">
            <div class="upgrade-item flex gap-3 justify-between py-4">
              <div class="font-500">{{ getFormatDateString(item.dateTime) }}</div>
              <div class="flex-1">{{ getFormatWeekdayString(item.dateTime) }}</div>
              <div class="min-w-fit whitespace-nowrap">{{ props.product?.currency_code }} {{ priceFormat(item.price) }}</div>
            </div>
            <hr />
          </template>
        </div>
        <div v-else class="upgrades-list">
          <template v-for="upgrade in upgrades" :key="upgrade.upgrade_id">
            <div class="upgrade-item flex gap-3 justify-between py-4">
              <div class="font-500">{{ upgrade.count }}x</div>
              <div class="flex-1">{{ upgrade?.name }}</div>
              <div class="min-w-fit whitespace-nowrap">{{ props.product?.currency_code }} {{ priceFormat(upgrade?.amount) }}</div>
            </div>
            <hr />
          </template>
        </div>
        <div class="additions-list">
          <template v-for="addition in additions" :key="addition.pid">
            <div class="addition-item flex gap-3 justify-between py-4">
              <div class="font-500">{{ addition.count }}x</div>
              <div class="flex-1">{{ addition?.name }}</div>
              <div class="min-w-fit">{{ props.product?.currency_code }} {{ priceFormat(addition?.amount) }}</div>
            </div>
            <hr />
          </template>
        </div>
      </div>
    </template>
    <template #footer="{ close }">
      <div class="actions py-3 px-5 w-full flex justify-end">
        <nd-button type="primary" class="<md:w-full" @click="close">{{ $t('all_confirm') }}</nd-button>
      </div>
    </template>
  </content-viewer>
</template>

<script setup lang="ts">
import { add, parseISO } from 'date-fns'

import ContentViewer from '@/components/common/content-viewer.vue'
import { priceFormat } from '@/utils/price'

import type { UiProductInfo, UiProductSelectedData } from '@/types/product'
import type { ProductUpgradeResource, ProductAdditionsResource } from '@/types/booking'

const { t } = useI18n()
const { format } = await useI18nDateFns()

const props = defineProps<{
  product: UiProductInfo
  bookingItem: UiProductSelectedData[]
  upgrades: ProductUpgradeResource[]
  additions: ProductAdditionsResource[]
}>()

const isSoldOut = inject<ComputedRef<boolean>>('isSoldOut')

const startDateTime = computed(() => {
  if (!props.bookingItem.length) return ''
  return props.bookingItem?.at?.(0)?.dateTime || ''
})

const endDateTime = computed(() => {
  if (!props.bookingItem.length) return ''
  return props.bookingItem?.at?.(-1)?.dateTime || ''
})

const startDateTimeString = computed(() => {
  if (!props.bookingItem.length) return ''
  return dateTimeStringFormatter(startDateTime.value) || ''
})

const endDateTimeString = computed(() => {
  try {
    if (!props.bookingItem.length) return ''
    if (props.product?.duration_text) return props.product.duration_text
    if (props.product?.checkout) {
      const [endDate, endTime] = endDateTime.value.split(' ')

      const realEndDate = isEarlierOrEqualInHourMinute(endTime!, props.product.checkout!)
        ? endDate!
        : format(add(parseISO(endDate!), { days: 1 }), 'yyyy-MM-dd')
      return dateTimeStringFormatter(`${realEndDate} ${props.product.checkout}`)
    }
    if (startDateTime.value !== endDateTime.value) return dateTimeStringFormatter(endDateTime.value) || ''

    const duration = Math.max(Number(props.product?.duration), ...props.upgrades.map(u => u.duration))
    return dateTimeStringFormatter(add(parseISO(startDateTime.value!), { minutes: duration }))
  } catch (error) {
    console.error('endDateTimeString: ', error)
    return ''
  }
})

const dayCount = computed(() => {
  return uiBookingItem.value.length
})

const totalPriceLabel = computed(() => {
  let label = t('total_billing_amount')
  if (props.product?.is_crossday && dayCount.value > 0) label += t('duration_in_nights', { nights: dayCount.value })

  return label
})

const additionsTotalPrice = computed(() => (props.additions?.length ? props.additions.reduce((acc, a) => acc + Number(a?.amount || 0), 0) : 0))
const upgradesTotalPrice = computed(() => (props.upgrades?.length ? props.upgrades.reduce((acc, u) => acc + Number(u?.amount || 0), 0) : 0))
const totalPrice = computed(() => additionsTotalPrice.value + upgradesTotalPrice.value)

const uiBookingItem = computed(() => {
  return props.bookingItem.slice(0, props.bookingItem.length === 1 ? 1 : -1)
})

const getFormatDateString = (dateTime: string) => {
  return format(parseISO(dateTime), 'MMM do')
}
const getFormatWeekdayString = (dateTime: string) => {
  return format(parseISO(dateTime), 'EEE')
}
const dateTimeStringFormatter = (dateTime?: string | Date) => {
  if (!dateTime) return ''
  if (typeof dateTime === 'string') dateTime = parseISO(dateTime)
  return format(dateTime, 'MMM do HH:mm')
}
</script>

<style lang="scss" scoped>
hr {
  @apply -mx-5 md:mx-0;
}
</style>
